import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import * as Form from 'components/form';
import { InputCopyButton } from 'components/input';
import * as SettingsCard from 'components/settings-card';
import { CAMPAIGN_SLUG_LINK } from 'constants/links';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface SlugFormValues {
    slug: string;
}

const CampaignSharingSlug = () => {
    const toast = useToast();
    const apiUrl = useAppState(state => state.apiUrl);
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const methods = Form.useGivebutterForm<SlugFormValues>({
        defaultValues: {
            slug: campaign?.slug ?? '',
        },
    });
    const [count, setCount] = useState(0);
    const {
        formState: { errors },
        trigger,
        watch,
        control,
        path,
        handleSubmit,
    } = methods;
    const slug = watch('slug');
    const computedSlug = `${apiUrl}/${slug}`;

    const { mutate: submit } = useMutation({
        mutationFn: (values: SlugFormValues) => {
            const data = {
                slug: values.slug,
            };

            return updateCampaign({ campaignId: campaign?.id!, data });
        },

        onSuccess: () => {
            toast.success('Slug updated!');
            setCount(c => c + 1);
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaign?.id!),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update slug'),
    });

    return (
        <Form.Provider {...methods}>
            <SettingsCard.Root>
                <SettingsCard.LeftSide>
                    <SettingsCard.Heading>General</SettingsCard.Heading>
                    <p className={'text-sm text-gray-700'}>
                        Choose a custom sharing link to personalize your campaign.{' '}
                        <a target="_blank" rel="noopener noreferrer" href={CAMPAIGN_SLUG_LINK}>
                            Read the guide
                        </a>
                        .
                    </p>
                </SettingsCard.LeftSide>
                <SettingsCard.RightSide>
                    <form>
                        <div className={'flex flex-col gap-4'}>
                            <Form.Container>
                                <Form.Label className={'text-base'} name={path('slug')}>
                                    Custom campaign link
                                    <Form.LabelAsterisk />
                                </Form.Label>
                                <div className={'flex flex-nowrap'}>
                                    <div
                                        className={
                                            'flex items-center rounded-bl-md rounded-tl-md bg-gray-100 px-3 py-2'
                                        }
                                    >
                                        <p className={'text-sm font-medium leading-none'}>
                                            {apiUrl}
                                        </p>
                                    </div>
                                    <Form.TextInputRoot
                                        control={control}
                                        name={'slug'}
                                        rules={{ required: 'A slug is required' }}
                                        inputRootProps={{
                                            className: 'input-right',
                                        }}
                                    >
                                        <Form.TextInputForm placeholder={'slug'} />
                                        <InputCopyButton text={computedSlug} />
                                    </Form.TextInputRoot>
                                </div>
                                <p className={'text-sm text-gray-700'}>
                                    Find your campaign page here:{' '}
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={computedSlug}
                                    >
                                        {computedSlug}
                                    </a>
                                </p>
                                <Form.Error name={path('slug')} />
                            </Form.Container>
                            <div>
                                <AlertDialog.Root
                                    key={count}
                                    open={Object.keys(errors).length > 0 ? false : undefined}
                                    type={'info'}
                                >
                                    <AlertDialog.Trigger asChild={true}>
                                        <Button variant="solid" onClick={() => trigger()}>
                                            Save
                                        </Button>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Are you sure you'd like to proceed?
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    Changing your campaign link will cause any
                                                    previous links to break. If you've already
                                                    shared your campaign link, we advise against
                                                    changing it.
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                <Button
                                                    variant="solid"
                                                    onClick={() =>
                                                        handleSubmit(
                                                            submit as SubmitHandler<SlugFormValues>,
                                                        )()
                                                    }
                                                >
                                                    Confirm
                                                </Button>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </div>
                        </div>
                    </form>
                </SettingsCard.RightSide>
            </SettingsCard.Root>
        </Form.Provider>
    );
};

export { CampaignSharingSlug };
